class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.detailsContainer = this.querySelector('details > *:last-child');
    this.summaryToggle = this.querySelector('summary');
    this.closeButton = this.querySelector('button[type="button"]');

    this.detailsContainer?.addEventListener(
      'transitionend',
      (event) => {
        if (event.propertyName === 'visibility' && !this.mainDetailsToggle.hasAttribute('open')) {
          this.detailsContainer.removeAttribute('style');
        }
      }
    );

    this.summaryToggle?.addEventListener('click', this.onSummaryClick.bind(this));
    if (this.closeButton) {
      this.closeButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.close();
      });
    }

    this.summaryToggle?.setAttribute('role', 'button');

    this.mainDetailsToggle?.addEventListener('toggle', this.onDetailsToggle.bind(this));
  }

  lockPageScroll() {
    // Desktop: do not set overflow:hidden on html/body — it breaks position:sticky
    // (e.g. PDP product image column). The fixed modal overlay blocks interaction.
  }

  unlockPageScroll() {}

  isMobileSearchViewport() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  onDetailsToggle() {
    if (this.isMobileSearchViewport()) {
      // Mobile uses Cloud Search UI — theme <details> modal must never open
      if (this.mainDetailsToggle.hasAttribute('open')) {
        this.mainDetailsToggle.removeAttribute('open');
      }
      return;
    }

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.lockPageScroll();
      const searchModal = this.mainDetailsToggle.querySelector('.search-modal');
      if (searchModal) searchModal.classList.add('search-modal--open');
      this.scheduleCloudSearchReposition();
    } else {
      this.unlockPageScroll();
      this.mainDetailsToggle.querySelector('.search-modal')?.classList.remove('search-modal--open');
      this.hideCloudSearchResults();
    }
  }

  getSearchInput() {
    return this.querySelector('.search__input, #Search-In-Modal');
  }

  hideCloudSearchResults() {
    const input = this.getSearchInput();
    if (input) input.value = '';

    document.getElementById('cloud-search-results')?.setAttribute('hidden', '');
  }

  triggerCloudSearchMobile() {
    const input = this.getSearchInput();
    if (!input) return;

    this.mainDetailsToggle.removeAttribute('open');
    input.click();
  }

  repositionCloudSearchResults() {
    if (this.isMobileSearchViewport()) return;

    const results = document.getElementById('cloud-search-results');
    const input = this.getSearchInput();
    if (!results || !input) return;

    const rect = input.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return;

    results.removeAttribute('hidden');
    results.style.removeProperty('display');
    results.classList.remove('cloud-search-mobile');

    const left = Math.round(rect.left);
    const top = Math.round(rect.bottom + 16);

    results.style.position = 'absolute';
    results.style.margin = '0';
    results.style.inset = `${top}px auto auto ${left}px`;
    results.style.transform = 'none';
    results.style.removeProperty('left');
    results.style.removeProperty('top');
    results.style.width = '400px';
    results.style.maxWidth = `calc(100vw - ${left}px - 16px)`;
    results.style.maxHeight = 'none';
    results.style.overflow = 'visible';
    results.style.zIndex = '10001';

    window.dispatchEvent(new Event('resize'));
  }

  ensureCloudSearchResultsObserver() {
    if (this._cloudSearchResultsObserver) return;

    const results = document.getElementById('cloud-search-results');
    if (!results) return;

    this._cloudSearchResultsObserver = new MutationObserver(() => {
      if (!this.isOpen() || this.isMobileSearchViewport()) return;

      const results = document.getElementById('cloud-search-results');
      const input = this.getSearchInput();
      if (!results || !input) return;

      const rect = input.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) return;

      const expectedTop = Math.round(rect.bottom + 16);
      const inset = results.style.inset || '';
      const match = inset.match(/^([\d.]+)px/);
      const currentTop = match ? Math.round(parseFloat(match[1])) : null;

      if (currentTop === expectedTop) return;

      this.repositionCloudSearchResults();
    });

    this._cloudSearchResultsObserver.observe(results, {
      attributes: true,
      attributeFilter: ['style', 'hidden'],
    });
  }

  scheduleCloudSearchReposition() {
    this.ensureCloudSearchResultsObserver();
    [0, 50, 200, 450].forEach((delay) => {
      setTimeout(() => this.repositionCloudSearchResults(), delay);
    });
  }

  isOpen() {
    return this.mainDetailsToggle.hasAttribute('open');
  }

  onSummaryClick(event) {
    if (this.isMobileSearchViewport()) {
      event.preventDefault();
      this.triggerCloudSearchMobile();
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.isOpen() ? this.closeModal(event, true) : this.openModal();
  }

  onBodyClick(event) {
    const isOverlayClick = event.target.classList.contains('modal-overlay');
    const isCloseButtonClick = event.target.closest('.search-modal__close-button');
    const isSearchContent = event.target.closest('.search-modal__content');

    if ((isOverlayClick && !isSearchContent) || isCloseButtonClick) {
      event.preventDefault();
      event.stopPropagation();

      this.hideCloudSearchResults();
      this.close(false);
    }
  }

  openModal() {
    if (this.isMobileSearchViewport()) return;

    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    document.body.addEventListener('click', this.onBodyClickEvent);

    this.lockPageScroll();

    this.mainDetailsToggle.setAttribute('open', '');

    const searchModal = this.mainDetailsToggle.querySelector('.search-modal');
    requestAnimationFrame(() => {
      if (searchModal) searchModal.classList.add('search-modal--open');
    });

    const firstFocusable = this.mainDetailsToggle.querySelector(
      'input:not([type="hidden"]), button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (firstFocusable) {
      firstFocusable.focus({ preventScroll: true });
    }

    this.scheduleCloudSearchReposition();

    const input = this.getSearchInput();
    if (input && !this._cloudSearchRepositionBound) {
      this._cloudSearchRepositionBound = true;
      input.addEventListener('focus', () => this.scheduleCloudSearchReposition());
    }
  }

  closeModal(event, animating = true) {
    if (this.isMobileSearchViewport()) return;

    if (event) event.preventDefault();
    document.body.removeEventListener('click', this.onBodyClickEvent);

    const searchModal = this.mainDetailsToggle.querySelector('.search-modal');
    if (searchModal) searchModal.classList.remove('search-modal--open');

    setTimeout(() => {
      this.mainDetailsToggle.removeAttribute('open');
      this.unlockPageScroll();
      this.hideCloudSearchResults();

      if (animating) {
        this.detailsContainer.style.setProperty('visibility', 'visible');
      }
    }, 300);
  }

  close(animating = true) {
    this.closeModal(null, animating);
  }
}

customElements.define('details-modal', DetailsModal);
